import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Books, Plus } from '@phosphor-icons/react';

const LIBRARIANS = ['global_admin', 'school_admin', 'principal', 'registrar', 'teacher'];

export default function Library() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isLib = LIBRARIANS.includes(user?.role);
  const canCatalog = ['global_admin', 'school_admin', 'principal', 'registrar'].includes(user?.role);
  const [q, setQ] = useState('');
  const { data: books } = useQuery({ queryKey: ['lib-books', q], queryFn: () => api.get('/library/books', { params: q ? { q } : {} }).then((r) => r.data) });
  const { data: loans } = useQuery({ queryKey: ['lib-loans'], queryFn: () => api.get('/library/loans').then((r) => r.data) });
  const { data: requests } = useQuery({ queryKey: ['lib-requests'], queryFn: () => api.get('/library/requests').then((r) => r.data) });
  const { data: overdue } = useQuery({ queryKey: ['lib-overdue'], queryFn: () => api.get('/library/overdue').then((r) => r.data), enabled: isLib });
  const { data: students } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data), enabled: isLib });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: () => api.get('/tenants').then((r) => r.data), enabled: isLib });
  const school = (tenants || []).find((t) => t.type === 'school');
  const [bookOpen, setBookOpen] = useState(false);
  const [bform, setBform] = useState({ title: '', author: '', isbn: '', category: 'general', copies_total: 1, cover_url: '' });
  const [looking, setLooking] = useState(false);
  const lookupIsbn = async () => {
    if (!bform.isbn.trim()) return toast.error('Type an ISBN first');
    setLooking(true);
    try {
      const r = await api.get('/library/isbn-lookup', { params: { isbn: bform.isbn } });
      if (r.data.found) {
        setBform((f) => ({ ...f, title: r.data.title, author: r.data.author, isbn: r.data.isbn, cover_url: r.data.cover_url || '' }));
        toast.success(`Found: ${r.data.title}`);
      } else toast.error('ISBN not found on OpenLibrary - enter details manually');
    } catch (e) { toast.error(errMsg(e)); }
    setLooking(false);
  };
  const [issueFor, setIssueFor] = useState(null);
  const [issueStudent, setIssueStudent] = useState('');

  const inval = () => { qc.invalidateQueries({ queryKey: ['lib-books'] }); qc.invalidateQueries({ queryKey: ['lib-loans'] }); qc.invalidateQueries({ queryKey: ['lib-overdue'] }); qc.invalidateQueries({ queryKey: ['lib-requests'] }); };
  const requestMut = useMutation({
    mutationFn: (book_id) => api.post('/library/requests', { book_id }),
    onSuccess: () => { toast.success('Borrow request sent to the librarian'); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const decideMut = useMutation({
    mutationFn: ({ id, action }) => api.post(`/library/requests/${id}/${action}`),
    onSuccess: (r, v) => { toast.success(v.action === 'approve' ? `Approved - book issued, due ${r.data.due_at}` : 'Request declined'); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const addMut = useMutation({
    mutationFn: () => api.post('/library/books', { ...bform, tenant_id: user?.tenant_id || school?.id, copies_total: parseInt(bform.copies_total, 10) || 1 }),
    onSuccess: () => { toast.success('Book added to catalog'); setBookOpen(false); setBform({ title: '', author: '', isbn: '', category: 'general', copies_total: 1, cover_url: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delBook = useMutation({ mutationFn: (id) => api.delete(`/library/books/${id}`), onSuccess: () => { toast.success('Book removed'); inval(); }, onError: (e) => toast.error(errMsg(e)) });
  const issueMut = useMutation({
    mutationFn: () => api.post('/library/issue', { book_id: issueFor.id, student_id: issueStudent }),
    onSuccess: (r) => { toast.success(`Issued - due ${r.data.due_at}`); setIssueFor(null); setIssueStudent(''); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const loanAct = useMutation({
    mutationFn: ({ id, action }) => api.post(`/library/loans/${id}/${action}`),
    onSuccess: (r, v) => {
      if (v.action === 'return') toast.success(r.data.fine > 0 ? `Returned ${r.data.days_late}d late - ₹${r.data.fine} fine posted to fees` : 'Returned on time');
      else toast.success(`Renewed - due ${r.data.due_at}`);
      inval();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const LoanRow = ({ l }) => (
    <div className="flex items-center gap-2 px-3 py-2 text-sm flex-wrap" data-testid={`loan-row-${l.id}`}>
      <span className="font-medium truncate">{l.book_title}</span>
      <span className="text-slate-400 text-xs">{l.student_name} · Grade {l.grade}</span>
      <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${l.status === 'issued' ? (l.due_at < new Date().toISOString().slice(0, 10) ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700') : 'bg-slate-100 text-slate-500'}`}>
        {l.status === 'issued' ? `due ${l.due_at}` : `returned${l.fine ? ` · ₹${l.fine} fine` : ''}`}
      </span>
      {isLib && l.status === 'issued' && (
        <span className="ml-auto flex gap-1.5">
          <button data-testid={`loan-renew-${l.id}`} onClick={() => loanAct.mutate({ id: l.id, action: 'renew' })} className="text-xs border border-slate-200 rounded px-2 py-1 hover:border-slate-900">Renew</button>
          <button data-testid={`loan-return-${l.id}`} onClick={() => loanAct.mutate({ id: l.id, action: 'return' })} className="text-xs bg-slate-900 text-white rounded px-2 py-1 hover:bg-slate-800">Return</button>
        </span>
      )}
    </div>
  );

  return (
    <div data-testid="library-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Catalog · loans · renewals · fines - fees</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2"><Books size={30} className="text-orange-600" weight="duotone" /> Library</h1>
        </div>
        {canCatalog && <Button data-testid="add-book-button" onClick={() => setBookOpen(true)} className="bg-slate-900 hover:bg-slate-800"><Plus size={16} className="mr-1" /> Add book</Button>}
      </div>

      {!isLib ? (
        <Tabs defaultValue="browse">
          <TabsList>
            <TabsTrigger value="browse" data-testid="library-tab-browse">Browse books</TabsTrigger>
            <TabsTrigger value="myloans" data-testid="library-tab-myloans">My loans{(loans || []).filter((l) => l.status === 'issued').length ? ` (${(loans || []).filter((l) => l.status === 'issued').length})` : ''}</TabsTrigger>
          </TabsList>
          <TabsContent value="browse" className="mt-4">
            <Input data-testid="book-search" placeholder="Search title, author or ISBN..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs mb-3 bg-white" />
            <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="student-catalog">
              {(books || []).map((b) => {
                const myReq = (requests || []).find((r) => r.book_id === b.id && r.status === 'pending');
                return (
                  <div key={b.id} className="flex items-center gap-2 px-3 py-2 text-sm flex-wrap" data-testid={`catalog-row-${b.id}`}>
                    {b.cover_url && <img src={b.cover_url} alt="" className="w-7 h-10 object-cover rounded shadow-sm" />}
                    <span className="font-medium truncate">{b.title}</span>
                    <span className="text-slate-400 text-xs">{b.author} · {b.category}</span>
                    <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${b.copies_available > 0 ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`}>{b.copies_available}/{b.copies_total} available</span>
                    <span className="ml-auto">
                      {myReq
                        ? <span className="font-mono-label text-[9px] uppercase bg-amber-100 text-amber-800 rounded px-1.5 py-0.5" data-testid={`request-pending-${b.id}`}>request pending</span>
                        : <button data-testid={`book-request-${b.id}`} disabled={b.copies_available < 1 || requestMut.isPending} onClick={() => requestMut.mutate(b.id)}
                            className="text-xs bg-orange-600 text-white rounded px-2 py-1 hover:bg-orange-500 disabled:opacity-40">Request to borrow</button>}
                    </span>
                  </div>
                );
              })}
              {(books || []).length === 0 && <p className="p-5 text-sm text-slate-400" data-testid="books-empty">No books in the catalog yet.</p>}
            </div>
            {(requests || []).length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 mt-4" data-testid="my-requests">
                <p className="font-mono-label text-[10px] uppercase text-slate-400 px-3 pt-3 pb-1">My requests</p>
                {(requests || []).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`my-request-${r.id}`}>
                    <span className="flex-1 truncate">{r.book_title} <span className="text-slate-400 text-xs">· {r.student_name}</span></span>
                    <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${r.status === 'pending' ? 'bg-amber-100 text-amber-800' : r.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="myloans" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="my-loans">
              {(loans || []).map((l) => <LoanRow key={l.id} l={l} />)}
              {(loans || []).length === 0 && <p className="p-5 text-sm text-slate-400">No books on loan.</p>}
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <Tabs defaultValue="books">
          <TabsList>
            {['books', 'requests', 'loans', 'overdue'].map((t) => {
              const pend = t === 'requests' ? (requests || []).filter((r) => r.status === 'pending').length : 0;
              return <TabsTrigger key={t} value={t} data-testid={`library-tab-${t}`} className="capitalize">{t}{t === 'overdue' && (overdue || []).length > 0 ? ` (${overdue.length})` : ''}{pend ? ` (${pend})` : ''}</TabsTrigger>;
            })}
          </TabsList>
          <TabsContent value="requests" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="requests-list">
              {(requests || []).map((r) => (
                <div key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm flex-wrap" data-testid={`request-row-${r.id}`}>
                  <span className="font-medium truncate">{r.book_title}</span>
                  <span className="text-slate-400 text-xs">{r.student_name} · Grade {r.grade}</span>
                  <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${r.status === 'pending' ? 'bg-amber-100 text-amber-800' : r.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`}>{r.status}</span>
                  {r.status === 'pending' && (
                    <span className="ml-auto flex gap-1.5">
                      <button data-testid={`request-approve-${r.id}`} onClick={() => decideMut.mutate({ id: r.id, action: 'approve' })} className="text-xs bg-green-700 text-white rounded px-2 py-1 hover:bg-green-600">Approve & issue</button>
                      <button data-testid={`request-reject-${r.id}`} onClick={() => decideMut.mutate({ id: r.id, action: 'reject' })} className="text-xs border border-rose-200 text-rose-600 rounded px-2 py-1 hover:bg-rose-50">Decline</button>
                    </span>
                  )}
                </div>
              ))}
              {(requests || []).length === 0 && <p className="p-5 text-sm text-slate-400" data-testid="requests-empty">No borrow requests yet.</p>}
            </div>
          </TabsContent>
          <TabsContent value="books" className="mt-4">
            <Input data-testid="book-search" placeholder="Search title, author or ISBN..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs mb-3 bg-white" />
            <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
              {(books || []).map((b) => (
                <div key={b.id} className="flex items-center gap-2 px-3 py-2 text-sm flex-wrap" data-testid={`book-row-${b.id}`}>
                  {b.cover_url && <img src={b.cover_url} alt="" className="w-7 h-10 object-cover rounded shadow-sm" />}
                  <span className="font-medium truncate">{b.title}</span>
                  <span className="text-slate-400 text-xs">{b.author}{b.isbn ? ` · ${b.isbn}` : ''} · {b.category}</span>
                  <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${b.copies_available > 0 ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`}>{b.copies_available}/{b.copies_total} available</span>
                  <span className="ml-auto flex gap-1.5">
                    <button data-testid={`book-issue-${b.id}`} disabled={b.copies_available < 1} onClick={() => setIssueFor(b)} className="text-xs bg-orange-600 text-white rounded px-2 py-1 hover:bg-orange-500 disabled:opacity-40">Issue</button>
                    {canCatalog && <button data-testid={`book-delete-${b.id}`} onClick={() => { if (window.confirm(`Remove "${b.title}" from catalog?`)) delBook.mutate(b.id); }} className="text-xs border border-rose-200 text-rose-600 rounded px-2 py-1 hover:bg-rose-50">Delete</button>}
                  </span>
                </div>
              ))}
              {(books || []).length === 0 && <p className="p-5 text-sm text-slate-400" data-testid="books-empty">Catalog is empty - add the first book.</p>}
            </div>
          </TabsContent>
          <TabsContent value="loans" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
              {(loans || []).map((l) => <LoanRow key={l.id} l={l} />)}
              {(loans || []).length === 0 && <p className="p-5 text-sm text-slate-400">No loans yet.</p>}
            </div>
          </TabsContent>
          <TabsContent value="overdue" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="overdue-list">
              {(overdue || []).map((l) => <LoanRow key={l.id} l={l} />)}
              {(overdue || []).length === 0 && <p className="p-5 text-sm text-slate-400">Nothing overdue 🎉</p>}
            </div>
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent data-testid="book-add-dialog">
          <DialogHeader><DialogTitle className="font-display">Add book</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input data-testid="book-isbn-input" placeholder="Scan or type ISBN..." value={bform.isbn} onChange={(e) => setBform({ ...bform, isbn: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && lookupIsbn()} />
              <Button data-testid="isbn-lookup-button" variant="outline" disabled={looking} onClick={lookupIsbn}>{looking ? 'Looking...' : 'Autofill'}</Button>
            </div>
            <p className="text-[11px] text-slate-400 -mt-1">Barcode scanners type the ISBN and press Enter - title, author and cover autofill via OpenLibrary.</p>
            <div className="flex gap-2 items-start">
              {bform.cover_url && <img src={bform.cover_url} alt="cover" data-testid="book-cover-preview" className="w-12 h-16 object-cover rounded shadow" />}
              <div className="flex-1 space-y-3">
                <Input data-testid="book-title-input" placeholder="Title" value={bform.title} onChange={(e) => setBform({ ...bform, title: e.target.value })} />
                <Input data-testid="book-author-input" placeholder="Author" value={bform.author} onChange={(e) => setBform({ ...bform, author: e.target.value })} />
              </div>
            </div>
            <Input data-testid="book-copies-input" type="number" className="w-28" placeholder="Copies" value={bform.copies_total} onChange={(e) => setBform({ ...bform, copies_total: e.target.value })} />
            <Button data-testid="book-add-submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!bform.title || addMut.isPending} onClick={() => addMut.mutate()}>Add to catalog</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!issueFor} onOpenChange={() => setIssueFor(null)}>
        <DialogContent data-testid="book-issue-dialog">
          <DialogHeader><DialogTitle className="font-display">Issue "{issueFor?.title}"</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select data-testid="issue-student-select" value={issueStudent} onChange={(e) => setIssueStudent(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
              <option value="">Select student...</option>
              {(students || []).filter((s) => s.status === 'active').map((s) => <option key={s.id} value={s.id}>{`${s.name} - Grade ${s.grade}${s.section}`}</option>)}
            </select>
            <p className="text-[11px] text-slate-400">14-day loan · 2 renewals max · ₹5/day fine posts straight to the student's fees on late return.</p>
            <Button data-testid="issue-confirm" className="w-full bg-orange-600 hover:bg-orange-500" disabled={!issueStudent || issueMut.isPending} onClick={() => issueMut.mutate()}>Issue book</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
